<script setup lang="ts">
import type { PostTypeSeo } from '~~/server/types/generated';

/* ======================================================
   ROUTER
   ------------------------------------------------------
   (Reserved for future query / route syncing if needed)
====================================================== */
const route = useRoute();
const router = useRouter();

/* ----------------------------------------------
   Helpers
---------------------------------------------- */
type AcademicTerm = 'Fall' | 'Winter' | 'Spring' | 'Summer';

/* ======================================================
   NORMALIZATION & ACADEMIC CALCULATIONS
   ------------------------------------------------------
   Converts raw CMS values into consistent academic logic
====================================================== */

/**
 * Normalize CMS academicSession values into canonical terms
 */
const normalizeTerm = (term: unknown): AcademicTerm | null => {
  if (Array.isArray(term)) {
    term = term[0]; // take first term
  }

  if (typeof term !== 'string') return null;

  const t = term.toLowerCase();

  if (t.includes('fall')) return 'Fall';
  if (t.includes('winter')) return 'Winter';
  if (t.includes('spring')) return 'Spring';
  if (t.includes('summer')) return 'Summer';

  return null;
};

/**
 * Determine academic year start for a term + date
 */
const getAcademicYearStart = (term: AcademicTerm, date: Date): number => {
  const year = date.getFullYear();
  const month = date.getMonth(); // Jan = 0

  switch (term) {
    case 'Fall':
      return year;
    case 'Winter':
      // Winter in Jan–Aug belongs to the academic year that started the previous Fall
      // Winter in Sep–Dec belongs to the academic year that started that same year
      return month >= 8 ? year : year - 1;
    case 'Spring':
    case 'Summer':
      // Spring/Summer are always in the "end year" of the academic year
      return year - 1;
  }
};
type AcademicYearBucket = {
  label: string; // "2025–2026"
  startYear: number;
  terms: Record<AcademicTerm, AcademicEvent[]>;
};

const TERM_ORDER: AcademicTerm[] = ['Fall', 'Winter', 'Spring', 'Summer'];

/**
 * Human-friendly heading for term cards
 * (Fall 2025, Winter 2025–2026, Spring 2026, etc.)
 */
const termYearLabel = (term: AcademicTerm, academicYearKey: string) => {
  const [startYear, endYear] = academicYearKey.split('-');

  switch (term) {
    case 'Fall':
      return `${term} ${startYear}`;
    case 'Winter':
      return `${term} ${startYear}–${endYear}`;
    case 'Spring':
    case 'Summer':
      return `${term} ${endYear}`;
  }
};

/* ======================================================
   API TYPES
====================================================== */
interface CptAcademicEvents {
  academicSession?: string;
  eventDateStart?: string;
  eventDateEnd?: string;
  eventDescription?: string;
}

interface AcademicEvent {
  id: string;
  title: string;
  slug: string;
  uri?: string;
  cptAcademicEvents?: CptAcademicEvents;
}

interface ImportantAnnouncementItem {
  id: string;
  title: string;
  slug: string;
  uri?: string;
  type: 'Academic' | 'Event';
  eventDateStart?: string | null;
  eventDescription?: string | null;
}

interface ImportantAnnouncementsModule {
  importantAnnouncements: boolean;
  importantAnnouncementsItems: ImportantAnnouncementItem[];
}

interface AcademicEventsHub {
  seo?: PostTypeSeo | null;
  heroHeading?: string;
  heroTeaser?: string;
  heroAlternateCalendarButton?: {
    __typename?: string;
    title?: string;
    uri?: string;
  };
  importantAnnouncementsModule?: ImportantAnnouncementsModule | null;
}

interface AcademicEventsApiResponse {
  hub: AcademicEventsHub | null;
  events: AcademicEvent[];
}

/* ======================================================
   DATA FETCH
====================================================== */
const { data } = await useAsyncData('academic-events-hub', async (nuxtApp) => {
  const url = `/api/academic-events/`;
  const response = await $fetch(url);
  return response as AcademicEventsApiResponse;
});

const hub = computed(() => data.value?.hub ?? null);
const events = computed(() => data.value?.events ?? []);

if (hub.value?.seo) {
  useBellevueSEO(hub.value.seo);
}

/* ======================================================
   DATE UTILITIES & FORMATTERS
====================================================== */
const parseCMSDate = (dateStr?: string) => {
  if (!dateStr) return null;

  // Extract YYYY-MM-DD from any ISO format
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;

  const [_, y, m, d] = match;
  // Construct a "no timezone" date: new Date(year, monthIndex, day)
  return new Date(Number(y), Number(m) - 1, Number(d));
};

const formatMonthDayShort = (dateStr?: string) => {
  const d = parseCMSDate(dateStr);
  return d
    ? d.toLocaleDateString('en-US', {
        month: 'short', // 👈 Jan, Feb, Mar
        day: 'numeric',
      })
    : '';
};

/**
 * Date range formatting logic used in event tables
 */
const formatEventDateRange = (start?: string, end?: string): string => {
  if (!start) return '';

  const startDate = parseCMSDate(start);
  const endDate = parseCMSDate(end);

  if (!startDate) return '';

  // No end date
  if (!endDate) {
    return formatMonthDayShort(start);
  }

  const sameMonth = startDate.getMonth() === endDate.getMonth() && startDate.getFullYear() === endDate.getFullYear();

  if (sameMonth) {
    return `${formatMonthDayShort(start)} – ${endDate.getDate()}`;
  }

  return `${formatMonthDayShort(start)} – ${formatMonthDayShort(end)}`;
};

const today = useToday();

const getEventLastRelevantDate = (event: AcademicEvent) => {
  const endDate = parseCMSDate(event.cptAcademicEvents?.eventDateEnd);
  if (endDate) return endDate;

  return parseCMSDate(event.cptAcademicEvents?.eventDateStart);
};

const termIsCurrentOrUpcoming = (termEvents: AcademicEvent[]) => {
  if (!termEvents.length) return false;

  const latestTermDate = termEvents.reduce<Date | null>((latest, event) => {
    const eventDate = getEventLastRelevantDate(event);
    if (!eventDate) return latest;
    if (!latest) return eventDate;
    return eventDate.getTime() > latest.getTime() ? eventDate : latest;
  }, null);

  if (!latestTermDate) return false;

  return latestTermDate.getTime() >= today.value.getTime();
};

/* ======================================================
   BUILD ACADEMIC YEAR BUCKETS
====================================================== */
const academicYears = computed<Record<string, AcademicYearBucket>>(() => {
  const map: Record<string, AcademicYearBucket> = {};

  for (const event of events.value) {
    const startRaw = event.cptAcademicEvents?.eventDateStart;
    const termRaw = event.cptAcademicEvents?.academicSession;

    if (!startRaw || !termRaw) continue;

    const date = parseCMSDate(startRaw);
    if (!date) continue;

    const term = normalizeTerm(termRaw);
    if (!term) continue;

    const startYear = getAcademicYearStart(term, date);
    const key = `${startYear}-${startYear + 1}`;

    if (!map[key]) {
      map[key] = {
        label: `${startYear}-${startYear + 1}`,
        startYear,
        terms: {
          Fall: [],
          Winter: [],
          Spring: [],
          Summer: [],
        },
      };
    }

    map[key].terms[term].push(event);
  }

  const filteredMap: Record<string, AcademicYearBucket> = {};

  for (const [key, yearBucket] of Object.entries(map)) {
    const filteredTerms: Record<AcademicTerm, AcademicEvent[]> = {
      Fall: [],
      Winter: [],
      Spring: [],
      Summer: [],
    };

    for (const term of TERM_ORDER) {
      const termEvents = yearBucket.terms[term];
      if (termIsCurrentOrUpcoming(termEvents)) {
        filteredTerms[term] = termEvents;
      }
    }

    const hasVisibleTerms = TERM_ORDER.some((term) => filteredTerms[term].length > 0);

    if (hasVisibleTerms) {
      filteredMap[key] = {
        ...yearBucket,
        terms: filteredTerms,
      };
    }
  }

  return filteredMap;
});

const academicYearList = computed<AcademicYearBucket[]>(() => {
  return Object.values(academicYears.value ?? {}).sort((a, b) => a.startYear - b.startYear);
});

const academicYearKey = (y: AcademicYearBucket) => `${y.startYear}-${y.startYear + 1}`;

type VisibleTermCard = {
  id: string;
  term: AcademicTerm;
  academicYearKey: string;
  label: string;
  events: AcademicEvent[];
};

const visibleTermCards = computed<VisibleTermCard[]>(() => {
  return academicYearList.value.flatMap((year) => {
    const yearKey = academicYearKey(year);

    return TERM_ORDER.filter((term) => year.terms[term].length > 0).map((term) => ({
      id: `${yearKey}-${term.toLowerCase()}`,
      term,
      academicYearKey: yearKey,
      label: termYearLabel(term, yearKey),
      events: year.terms[term],
    }));
  });
});

const sortByDateAsc = (list: AcademicEvent[]) =>
  [...list].sort((a, b) => {
    const aDate = parseCMSDate(a.cptAcademicEvents?.eventDateStart)?.getTime() ?? 0;
    const bDate = parseCMSDate(b.cptAcademicEvents?.eventDateStart)?.getTime() ?? 0;
    return aDate - bDate;
  });
</script>

<template>
  <div
    class="scroll-mt-15"
    style="
      background:
        url('/images/bg-right-pattern.svg') right no-repeat,
        linear-gradient(184deg, #fbf5e7 1.28%, #fff 94.01%);
      background-size: auto, auto;
      overflow: hidden;
      background-position: top right;
    "
  >
    <LayoutsContainer padding-top="larger">
      <!-- Page Hero -->
      <div class="flex flex-col flex-nowrap gap-6 md:flex-row md:items-stretch md:gap-32">
        <div class="w-full md:w-2/6">
          <TypographyTitle size="xl" as="h1" :html="hub?.heroHeading" />
        </div>

        <div class="flex w-full flex-col items-start md:w-4/6">
          <div
            class="hero-teaser text-brand-600 mt-5 mb-8 leading-[28px] font-medium md:text-xl md:leading-[26px] md:font-black md:-tracking-[0.4px]"
            v-html="hub?.heroTeaser"
          />

          <BaseButton
            v-if="hub?.heroAlternateCalendarButton"
            :to="hub.heroAlternateCalendarButton.uri"
            variant="primary"
            class="mt-auto"
          >
            {{ hub.heroAlternateCalendarButton.title }}
          </BaseButton>
        </div>
      </div>

      <BaseDivider class="max-md:mt-6 max-md:mb-10 md:my-16" />

      <ImportantAnnouncements :module="hub?.importantAnnouncementsModule" />

      <div class="flex flex-col gap-8">
        <!-- EVENTS BY TERM -->
        <div class="grid gap-8 sm:max-md:grid-cols-2 lg:grid-cols-2">
          <template v-if="visibleTermCards.length">
            <div
              v-for="card in visibleTermCards"
              :key="card.id"
              class="rounded-2xl bg-white p-6 pb-2 md:px-8 md:py-10"
              style="
                box-shadow:
                  5px 5px 1px 0 rgba(219, 170, 49, 0.1),
                  10px 15px 25px 0 rgba(220, 174, 58, 0.07);
              "
            >
              <!-- TERM HEADER -->
              <TypographyTitle size="md" as="h3" class="border-accent-500/[0.5] mb-2 border-b-2 pb-6 lowercase">
                {{ card.label }}
              </TypographyTitle>

              <!-- EVENTS LIST -->
              <table v-if="card.events.length" class="w-full table-fixed">
                <colgroup>
                  <col class="w-[100px] md:w-[120px]" />
                  <col />
                </colgroup>
                <tbody>
                  <tr
                    v-for="event in sortByDateAsc(card.events)"
                    :key="event.id"
                    class="border-brand-500/[0.1] border-b last:border-none"
                  >
                    <!-- DATE -->
                    <td class="text-brand-600 py-4 pr-4 align-top font-bold max-md:text-xs">
                      {{
                        formatEventDateRange(
                          event.cptAcademicEvents?.eventDateStart,
                          event.cptAcademicEvents?.eventDateEnd,
                        )
                      }}
                    </td>

                    <!-- DESCRIPTION -->
                    <td class="text-brand-900/[0.8] py-4 align-top *:-mt-0.5 max-md:text-xs">
                      <div :key="`desc-${event.id}`" class="*:max-md:text-xs" v-html="event.cptAcademicEvents?.eventDescription" />
                    </td>
                  </tr>
                </tbody>
              </table>

              <!-- EMPTY STATE -->
              <p v-else class="text-brand-500 pt-2 pb-4 text-sm italic">TBD</p>
            </div>
          </template>

          <p v-else class="text-brand-700 col-span-full text-lg font-medium">
            No upcoming academic events are available.
          </p>
        </div>
      </div>
    </LayoutsContainer>
  </div>
</template>

<style scoped>
:deep(.hero-teaser p) {
  margin: 0;
  font-size: inherit;
  line-height: inherit;
  font-weight: inherit;
  color: inherit;
}
:deep(.event-description p) {
  color: #543a76;
  font-size: 20px;
  font-weight: 900;
}
</style>
